import { useEffect, useState } from 'react';
import axios from 'axios';
import { Eye, Users, TrendingUp, Calendar, ArrowUp, ArrowDown, Minus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsCard = ({ label, value, icon: Icon, color, comparison, comparisonLabel }) => {
  const getComparisonIcon = () => {
    if (!comparison && comparison !== 0) return null;
    if (comparison > 0) return <ArrowUp className="h-3 w-3 text-green-500" />;
    if (comparison < 0) return <ArrowDown className="h-3 w-3 text-red-500" />;
    return <Minus className="h-3 w-3 text-zinc-500" />;
  };

  const getComparisonColor = () => {
    if (!comparison && comparison !== 0) return 'text-zinc-500';
    if (comparison > 0) return 'text-green-500';
    if (comparison < 0) return 'text-red-500';
    return 'text-zinc-500';
  };

  return (
    <div className="bg-zinc-900/50 border border-white/10 p-4 lg:p-6">
      <div className="flex items-center justify-between mb-2 lg:mb-4">
        <span className="text-zinc-400 text-xs lg:text-sm uppercase tracking-wider">{label}</span>
        <Icon className={`h-4 w-4 lg:h-6 lg:w-6 ${color}`} />
      </div>
      <p className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white">{value}</p>
      {(comparison !== undefined && comparison !== null) && (
        <div className="flex items-center gap-1 mt-2">
          {getComparisonIcon()}
          <span className={`text-xs ${getComparisonColor()}`}>
            {comparison > 0 ? '+' : ''}{comparison}% {comparisonLabel}
          </span>
        </div>
      )}
    </div>
  );
};

const VisitorChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-48 lg:h-64 flex items-center justify-center text-zinc-500">
        Nenhum dado disponível
      </div>
    );
  }

  const maxVisits = Math.max(...data.map(d => d.visits), 1);

  return (
    <div className="h-48 lg:h-64 flex items-end gap-1">
      {data.map((day, index) => {
        const height = (day.visits / maxVisits) * 100;
        const uniqueHeight = (day.unique / maxVisits) * 100;
        const date = new Date(day.date);
        const dayLabel = `${date.getDate()}/${date.getMonth() + 1}`;
        
        return (
          <div key={index} className="flex-1 flex flex-col items-center group relative">
            <div className="w-full flex flex-col items-center" style={{ height: '100%' }}>
              {/* Tooltip */}
              <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-black border border-white/20 text-white text-xs px-3 py-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                <div className="font-bold mb-1">{dayLabel}</div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                  <span>Visitas: {day.visits}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                  <span>Únicos: {day.unique}</span>
                </div>
              </div>
              
              {/* Bars container */}
              <div className="w-full h-full flex items-end justify-center gap-0.5">
                {/* Total visits bar */}
                <div 
                  className="w-1/2 bg-primary/60 hover:bg-primary transition-colors cursor-pointer rounded-t"
                  style={{ height: `${height}%`, minHeight: day.visits > 0 ? '4px' : '0' }}
                />
                {/* Unique visitors bar */}
                <div 
                  className="w-1/2 bg-blue-500/60 hover:bg-blue-500 transition-colors cursor-pointer rounded-t"
                  style={{ height: `${uniqueHeight}%`, minHeight: day.unique > 0 ? '4px' : '0' }}
                />
              </div>
            </div>
            <span className="text-[8px] lg:text-[10px] text-zinc-500 mt-1 truncate w-full text-center">
              {dayLabel}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const VisitorAnalytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-zinc-900/50 border border-white/10 p-4 lg:p-6 mb-6">
        <div className="flex items-center justify-center py-8">
          <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (!analytics) {
    return null;
  }

  // Calculate comparison percentages
  const calcComparison = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const todayVsYesterday = calcComparison(analytics.today, analytics.yesterday);
  const weekVsLastWeek = calcComparison(analytics.this_week, analytics.last_week);
  const monthVsLastMonth = calcComparison(analytics.this_month, analytics.last_month);

  return (
    <div className="mb-6 lg:mb-8" data-testid="visitor-analytics">
      <h2 className="font-heading text-xl lg:text-2xl font-bold text-white mb-4 lg:mb-6 flex items-center gap-2">
        <Eye className="h-6 w-6 text-primary" />
        Análise de Visitantes
      </h2>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <AnalyticsCard
          label="Hoje"
          value={analytics.today}
          icon={Eye}
          color="text-primary"
          comparison={todayVsYesterday}
          comparisonLabel="vs ontem"
        />
        <AnalyticsCard
          label="Esta Semana"
          value={analytics.this_week}
          icon={Calendar}
          color="text-blue-500"
          comparison={weekVsLastWeek}
          comparisonLabel="vs semana anterior"
        />
        <AnalyticsCard
          label="Este Mês"
          value={analytics.this_month}
          icon={TrendingUp}
          color="text-green-500"
          comparison={monthVsLastMonth}
          comparisonLabel="vs mês anterior"
        />
        <AnalyticsCard
          label="Visitantes Únicos (Mês)"
          value={analytics.unique_visitors_month}
          icon={Users}
          color="text-purple-500"
        />
      </div>

      {/* Chart */}
      <div className="bg-zinc-900/50 border border-white/10 p-4 lg:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-bold text-white">Visitas dos Últimos 14 Dias</h3>
          <div className="flex items-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 bg-primary rounded"></span>
              <span className="text-zinc-400">Total</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 bg-blue-500 rounded"></span>
              <span className="text-zinc-400">Únicos</span>
            </div>
          </div>
        </div>
        <VisitorChart data={analytics.visits_by_day} />
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-3 mt-4">
        <div className="bg-zinc-900/30 border border-white/5 p-3 text-center">
          <p className="text-zinc-500 text-xs mb-1">Únicos Hoje</p>
          <p className="font-bold text-white text-lg">{analytics.unique_visitors_today}</p>
        </div>
        <div className="bg-zinc-900/30 border border-white/5 p-3 text-center">
          <p className="text-zinc-500 text-xs mb-1">Únicos Semana</p>
          <p className="font-bold text-white text-lg">{analytics.unique_visitors_week}</p>
        </div>
        <div className="bg-zinc-900/30 border border-white/5 p-3 text-center">
          <p className="text-zinc-500 text-xs mb-1">Ontem</p>
          <p className="font-bold text-white text-lg">{analytics.yesterday}</p>
        </div>
      </div>
    </div>
  );
};

export default VisitorAnalytics;
